import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LayoutContainer from 'components/Common/LayoutContainer'
import ChatLandingFloatingImages from './ChatLandingFloatingImages'
import ChatLandingHero from './ChatLandingHero'
import ChatLandingSuggestedActionTile from './ChatLandingSuggestedActionTile'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useChatList, useDeleteChatMutation, useRenameChatMutation } from 'tripPlanner/hooks/api/chat'
import { useDeleteTrip, useRecentTrips, useUpdateTrip } from 'tripPlanner/hooks/api/trip'
import Group from 'components/utils/Group'
import ChatLandingRecentChatTile from './ChatLandingRecentChatTile'
import ChatLandingUpcomingOrderTile from './ChatLandingUpcomingOrderTile'
import ChatLandingExperienceOrderTile from './ChatLandingExperienceOrderTile'
import ChatLandingFlightOrderTile from './ChatLandingFlightOrderTile'
import ChatLandingRecentSearchTile from './ChatLandingRecentSearchTile'
import ChatLandingViewedOfferTile from './ChatLandingViewedOfferTile'
import useOrders from 'hooks/Order/useOrders'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import { getHighIntentHotelOffers } from 'selectors/recomendations/highIntentSelectors'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import { isOrderEligibleForChatTrip } from 'tripPlanner/hooks/orderChatTripEligibility'
import { isItemCancelled, sortOrdersByDeparture } from 'lib/order/orderUtils'
import { reportClientError } from 'services/errorReportingService'
import { trackEntryPoint, type EntryPointType } from 'tripPlanner/utils/chatAnalytics'
import { getCheckInCheckOutText } from 'components/Search/utils'
import { buildBookingTag, getFlightBookingItemIds, getFlightDetailsStateByItemIds, getOrderTileDataList, type OrderTileData } from './bookingSelectionUtils'
import { canAccessStaffChat } from 'tripPlanner/utils/staffChatAccess'
import StaffChatAccessForm from './StaffChatAccessForm'
import {
  buildBookingContextSections,
  buildScopedOrder,
  type SelectedBooking,
} from 'tripPlanner/components/Chat/AddBookingModal'
import {
  appendContextTag,
  TRIP_PLANNER_ACTION_CONTEXT_LABEL,
} from 'tripPlanner/utils/contextTag'
import { buildRecentChatItems } from 'tripPlanner/pages/chat/recentChatItems'
import { CHAT_TRIP_PLANNER_ROUTE } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useChatNavigation'
import { getTripURL } from 'tripPlanner/utils'
import useNavigateTo from 'hooks/useNavigateTo'
import ConfirmDeleteChatModal from 'tripPlanner/components/Chat/ConfirmDeleteChatModal'
import type { RecentChatItem } from 'tripPlanner/pages/chat/recentChatItems'
import useUserMessageSubtitles from '../useUserMessageSubtitles'

const LandingContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

const TopArea = styled.div`
  position: relative;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(80)} 0;
  overflow: hidden;
`

const ContentWrapper = styled(LayoutContainer)`
  position: relative;
  z-index: 1;
  padding: ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(24)};
`

const RecentChatsSection = styled.section`
  position: relative;
  z-index: 2;
  width: 100%;
  flex-shrink: 0;
  padding: ${rem(32)} ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const RecentChatsContent = styled.div`
  width: 100%;
  max-width: ${rem(1140)};
  margin: 0 auto;
`

const RecentChatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${rem(16)};

  ${mediaQueryDown.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mediaQueryDown.mobile} {
    grid-template-columns: minmax(0, 1fr);
  }
`

const StaffToolsSection = styled.section`
  position: relative;
  z-index: 2;
  width: 100%;
  flex-shrink: 0;
  padding: ${rem(40)} ${rem(32)} ${rem(64)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const StaffToolsContent = styled.div`
  width: 100%;
  max-width: ${rem(1140)};
  margin: 0 auto;
`

type CardFilter = 'bookings' | 'recentSearches' | 'viewedOffers' | 'suggestions'
const MAX_BOOKING_TILES = 12

interface CardFilterOption {
  id: CardFilter
  title: string
}

type CarouselTile =
  | { type: 'booking'; data: OrderTileData }
  | { type: 'recentSearch'; data: { id: string; locationName: string; checkInDate?: string; checkOutDate?: string; totalAdults?: number } }
  | { type: 'viewedOffer'; offerId: string }
  | { type: 'suggestedAction'; label: string }

const INITIAL_VISIBLE_RECENT_CHATS = 6

function getViewedOfferTags(propertyName: string, location?: string): Array<string> {
  const parts: Array<string> = [propertyName]
  if (location) {
    parts.push(location)
  }
  return parts
}

function collectSelectedBookings(
  orders: Array<App.Order>,
  selectedItemIds: ReadonlySet<string>,
): Array<SelectedBooking> {
  const selectedBookings: Array<SelectedBooking> = []

  for (const order of orders) {
    for (const item of order.items) {
      if (isItemCancelled(item) || !selectedItemIds.has(item.id)) continue
      selectedBookings.push({
        order: buildScopedOrder(order, { items: [item] }),
        offers: [],
        selectionKey: `hotel:${order.id}:${item.id}`,
        bookingItemId: item.id,
      })
    }
    for (const item of order.bedbankItems) {
      if (isItemCancelled(item) || !selectedItemIds.has(item.id)) continue
      selectedBookings.push({
        order: buildScopedOrder(order, { bedbankItems: [item] }),
        offers: [],
        selectionKey: `bedbank:${order.id}:${item.id}`,
        bookingItemId: item.id,
      })
    }
    for (const item of order.flightItems) {
      if (isItemCancelled(item) || !selectedItemIds.has(item.id)) continue
      selectedBookings.push({
        order: buildScopedOrder(order, { flightItems: [item] }),
        offers: [],
        selectionKey: `flight:${order.id}:${item.id}`,
        bookingItemId: item.id,
      })
    }
    for (const item of order.experienceItems) {
      if (isItemCancelled(item) || !selectedItemIds.has(item.id)) continue
      selectedBookings.push({
        order: buildScopedOrder(order, { experienceItems: [item] }),
        offers: [],
        selectionKey: `experience:${order.id}:${item.id}`,
        bookingItemId: item.id,
      })
    }
  }

  return selectedBookings
}

function shuffleArray<T>(array: Array<T>): Array<T> {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[j]
    shuffled[j] = temp
  }
  return shuffled
}

export interface BookingSelection {
  orderIds: Array<string>
  offerIds: Array<string>
  itemIds: Array<string>
}

interface Props {
  onSubmit: (message: string) => void
  onStaffChatOpen?: (chatId: number) => void
  onAddTags?: (tags: Array<string>) => void
  onBookingItemsAdded?: (bookingItemIds: Array<string>) => void
  disabled?: boolean
  selectedTags?: Array<string>
  onRemoveTag?: (tag: string) => void
  onBookingSelectionChange?: (selection: BookingSelection) => void
  className?: string
}

function ChatLandingPage({
  onSubmit,
  onStaffChatOpen,
  onAddTags,
  onBookingItemsAdded,
  disabled = false,
  selectedTags,
  onRemoveTag,
  onBookingSelectionChange,
  className,
}: Props) {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const accountEmail = useAppSelector(state => state.auth.account.email)
  const showModal = useContext(ModalContext)
  const {
    data,
    refetch: refetchChatList,
  } = useChatList({ enabled: isLoggedIn, staleTime: 30_000 })

  const intl = useIntl()
  const navigateTo = useNavigateTo()
  const [upcomingOrders, ordersFetched] = useOrders('upcoming')
  const { data: previousTrips = [], refetch: refetchTrips } = useRecentTrips({ enabled: isLoggedIn })
  const recentSearches = useRecentSearches()
  const previouslyViewedOffers = useAppSelector(getHighIntentHotelOffers)
  const { mutateAsync: deleteChatMutation } = useDeleteChatMutation()
  const { mutateAsync: renameChatMutation } = useRenameChatMutation()
  const { mutateAsync: deleteTripMutation } = useDeleteTrip({
    onSuccess: () => {
      refetchTrips()
    },
    onError: (error) => {
      console.error('[TripChat] Failed to delete trip from landing page', error)
      showSnackbar(intl.formatMessage({
        id: 'FtZ79RamZe2rLll_1NZF',
        defaultMessage: 'Unable to delete trip. Please try again.',
        description: 'Error snackbar message when trip deletion fails in the trip planner AI chat',
      }), 'critical')
    },
  })
  const { mutateAsync: updateTripMutation } = useUpdateTrip({
    onError: (error) => {
      console.error('[TripChat] Failed to rename trip from landing page', error)
      showSnackbar(intl.formatMessage({
        id: '5NH9uh8HGpEvAfHkilEf',
        defaultMessage: 'Unable to rename trip. Please try again.',
        description: 'Error snackbar message when trip rename fails in the trip planner AI chat',
      }), 'critical')
    },
  })

  const viewedOfferIds = useMemo(() => {
    return previouslyViewedOffers.slice(0, 3).map(offer => offer.offerId)
  }, [previouslyViewedOffers])

  const bookingTiles = useMemo<Array<OrderTileData>>(() => {
    if (!ordersFetched) return []
    const eligible = upcomingOrders.filter(isOrderEligibleForChatTrip)
    const sorted = sortOrdersByDeparture(eligible)
    return sorted.flatMap(getOrderTileDataList).slice(0, MAX_BOOKING_TILES)
  }, [ordersFetched, upcomingOrders])

  const bookingFlightItemIds = useMemo(() => {
    return getFlightBookingItemIds(bookingTiles)
  }, [bookingTiles])

  const flightDetailsState = useAppSelector((state) => {
    return getFlightDetailsStateByItemIds(state.orders.flightDetails, bookingFlightItemIds)
  }, shallowEqual)

  const recentSearchTiles = useMemo(() => {
    return recentSearches.slice(0, 3).map(search => {
      const totalAdults = search.rooms.reduce((sum, room) => sum + (room.adults ?? 0), 0)
      return {
        id: search.recentSearchId,
        locationName: search.searchItem.format.mainText,
        checkInDate: search.dates.checkIn,
        checkOutDate: search.dates.checkOut,
        totalAdults: totalAdults || undefined,
      }
    })
  }, [recentSearches])

  const suggestedActions = useMemo(() => {
    if (bookingTiles.length > 0) {
      return [
        intl.formatMessage({ id: '4Z28PhruhsoRynk_1pDQ', defaultMessage: 'What should I add to this stay?', description: 'Quick action pill when an order is present on the chat landing page' }),
        intl.formatMessage({ id: 'yUTa_ndVqkTREpBIInVR', defaultMessage: 'Help me make the most of these dates', description: 'Quick action pill when an order is present on the chat landing page' }),
        intl.formatMessage({ id: 'wRqFE56_R0Z4vEQZJazx', defaultMessage: 'Add activities and restaurants near my hotel', description: 'Quick action pill when an order is present on the chat landing page' }),
        intl.formatMessage({ id: '862OnDklLTJYvRCINrXh', defaultMessage: 'Create a day-by-day plan for this trip', description: 'Quick action pill when an order is present on the chat landing page' }),
      ]
    }
    return [
      intl.formatMessage({ id: 'bOWGp05rGbRD2ETaAw6b', defaultMessage: 'Take a tour of TripPlanner', description: 'Quick action button label to start a guided tour of the TripPlanner feature' }),
      intl.formatMessage({ id: 'BjPZOZzTziLpw1ywsGGo', defaultMessage: 'Where should I go?', description: 'Quick action button label to get destination suggestions from TripPlanner AI' }),
      intl.formatMessage({ id: 'oWIAGH5r6qOgN3HXJrXh', defaultMessage: 'Local trip ideas', description: 'Quick action button label to get local trip ideas from the TripPlanner AI' }),
      intl.formatMessage({ id: 'm3p5axiC0ZIbPox_-doP', defaultMessage: 'Weekend escapes', description: 'Quick action button label to get weekend escape ideas from TripPlanner AI' }),
    ]
  }, [bookingTiles.length, intl])

  const [selectedFilter, setSelectedFilter] = useState<CardFilter | undefined>()
  const [modalAddedBookingItemIds, setModalAddedBookingItemIds] = useState<Array<string>>([])
  const [isSubmittingBookings, setIsSubmittingBookings] = useState(false)
  const [showAllRecentChats, setShowAllRecentChats] = useState(false)

  const filterOptions = useMemo<Array<CardFilterOption>>(() => {
    const options: Array<CardFilterOption> = []

    if (isLoggedIn && bookingTiles.length > 0) {
      options.push({
        id: 'bookings',
        title: intl.formatMessage({ id: 'BW263IdRuVxs-vDKap2d', defaultMessage: 'Your bookings', description: 'Filter chip label for bookings on the chat landing page' }),
      })
    }
    if (recentSearchTiles.length > 0) {
      options.push({
        id: 'recentSearches',
        title: intl.formatMessage({ id: 'Ri9ED07d8dIKeE2InE_G', defaultMessage: 'Recent searches', description: 'Filter chip label for recent searches on the chat landing page' }),
      })
    }
    if (viewedOfferIds.length > 0) {
      options.push({
        id: 'viewedOffers',
        title: intl.formatMessage({ id: 'nDHRM5wRj7Wu8i7KRD_P', defaultMessage: 'Previously viewed offers', description: 'Filter chip label for previously viewed offers on the chat landing page' }),
      })
    }
    options.push({
      id: 'suggestions',
      title: intl.formatMessage({ id: 'ZkUz3z0NpTKmYQ7P5CFC', defaultMessage: 'Suggestions', description: 'Filter chip label for suggested actions on the chat landing page' }),
    })

    return options
  }, [isLoggedIn, bookingTiles.length, recentSearchTiles.length, viewedOfferIds.length, intl])

  useEffect(() => {
    if (selectedFilter && !filterOptions.some(opt => opt.id === selectedFilter)) {
      setSelectedFilter(undefined)
    }
  }, [filterOptions, selectedFilter])

  const handleFilterClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const filterId = e.currentTarget.dataset.filter as CardFilter
    setSelectedFilter(prev => prev === filterId ? undefined : filterId)
  }, [])

  const tileDataKey = useMemo(() => {
    const bookingIds = bookingTiles.map(t => t.itemId).join(',')
    const searchIds = recentSearchTiles.map(t => t.id).join(',')
    const offerIds = viewedOfferIds.join(',')
    return `${bookingIds}|${searchIds}|${offerIds}|${suggestedActions.length}`
  }, [bookingTiles, recentSearchTiles, viewedOfferIds, suggestedActions])

  const shuffledTilesRef = useRef<{ key: string; tiles: Array<CarouselTile> }>({ key: '', tiles: [] })
  if (shuffledTilesRef.current.key !== tileDataKey) {
    const bookings: Array<CarouselTile> = bookingTiles.map(data => ({ type: 'booking' as const, data }))
    const rest: Array<CarouselTile> = [
      ...recentSearchTiles.map(data => ({ type: 'recentSearch' as const, data })),
      ...viewedOfferIds.map(offerId => ({ type: 'viewedOffer' as const, offerId })),
      ...suggestedActions.map(label => ({ type: 'suggestedAction' as const, label })),
    ]
    shuffledTilesRef.current = { key: tileDataKey, tiles: [...bookings, ...shuffleArray(rest)] }
  }
  const shuffledAllTiles = shuffledTilesRef.current.tiles

  const { bookingTagsByItemId, bookingDataByTag, bookingDataByItemId } = useMemo(() => {
    const tagsByItemId = new Map<string, string>()
    const dataByTag = new Map<string, OrderTileData>()
    const dataByItemId = new Map<string, OrderTileData>()
    for (const tile of bookingTiles) {
      const tag = buildBookingTag({ intl, tile, flightDetailsState })
      tagsByItemId.set(tile.itemId, tag)
      dataByTag.set(tag, tile)
      dataByItemId.set(tile.itemId, tile)
    }
    return {
      bookingTagsByItemId: tagsByItemId,
      bookingDataByTag: dataByTag,
      bookingDataByItemId: dataByItemId,
    }
  }, [bookingTiles, flightDetailsState, intl])

  const prevBookingKeyRef = useRef('')

  useEffect(() => {
    const orderIdSet = new Set<string>()
    const offerIdSet = new Set<string>()
    const itemIdSet = new Set<string>()
    for (const tag of selectedTags ?? []) {
      const tile = bookingDataByTag.get(tag)
      if (tile) {
        orderIdSet.add(tile.orderId)
        offerIdSet.add(tile.offerId)
        itemIdSet.add(tile.itemId)
      }
    }
    for (const itemId of modalAddedBookingItemIds) {
      const tile = bookingDataByItemId.get(itemId)
      if (tile) {
        orderIdSet.add(tile.orderId)
        offerIdSet.add(tile.offerId)
        itemIdSet.add(tile.itemId)
      }
    }
    const orderIds = [...orderIdSet].sort()
    const offerIds = [...offerIdSet].sort()
    const itemIds = [...itemIdSet].sort()
    const key = `${orderIds.join(',')}|${offerIds.join(',')}|${itemIds.join(',')}`
    if (key !== prevBookingKeyRef.current) {
      prevBookingKeyRef.current = key
      onBookingSelectionChange?.({ orderIds, offerIds, itemIds })
    }
  }, [selectedTags, onBookingSelectionChange, bookingDataByTag, bookingDataByItemId, modalAddedBookingItemIds])

  const getRecentSearchTags = useCallback((locationName: string, checkInDate?: string, checkOutDate?: string, totalAdults?: number) => {
    const parts: Array<string> = [locationName]
    if (checkInDate && checkOutDate) {
      parts.push(getCheckInCheckOutText(checkInDate, checkOutDate))
    }
    if (totalAdults) {
      parts.push(intl.formatMessage(
        {
          id: '8Wb0texqenDfYTvPGAQI',
          defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
          description: 'Pluralised adult count label for recent search tile on chat page',
        },
        { count: totalAdults },
      ))
    }
    return parts
  }, [intl])

  const entryPointFiredRef = useRef(false)

  const fireEntryPoint = useCallback((type: EntryPointType) => {
    if (!entryPointFiredRef.current) {
      entryPointFiredRef.current = true
      trackEntryPoint(type)
    }
  }, [])

  const selectedTagSet = useMemo(() => new Set(selectedTags ?? []), [selectedTags])
  const modalAddedBookingItemIdSet = useMemo(() => {
    return new Set(modalAddedBookingItemIds)
  }, [modalAddedBookingItemIds])
  const selectedBookingItemIds = useMemo(() => {
    const itemIds = new Set(modalAddedBookingItemIds)

    for (const [tag, tile] of bookingDataByTag.entries()) {
      if (selectedTagSet.has(tag)) {
        itemIds.add(tile.itemId)
      }
    }

    return [...itemIds]
  }, [bookingDataByTag, modalAddedBookingItemIds, selectedTagSet])

  const handleBookingItemsAdded = useCallback((bookingItemIds: Array<string>) => {
    if (bookingItemIds.length === 0) {
      return
    }

    setModalAddedBookingItemIds((previous) =>
      [...new Set([...previous, ...bookingItemIds])],
    )
    onBookingItemsAdded?.(bookingItemIds)
  }, [onBookingItemsAdded])

  const toggleTag = useCallback((tag: string) => {
    if (selectedTagSet.has(tag)) {
      onRemoveTag?.(tag)
    } else {
      onAddTags?.([tag])
    }
  }, [selectedTagSet, onRemoveTag, onAddTags])

  const handleBookingTileClick = useCallback((itemId: string) => {
    const tag = bookingTagsByItemId.get(itemId)
    if (!tag) return
    fireEntryPoint('booking_card')
    toggleTag(tag)
  }, [bookingTagsByItemId, toggleTag, fireEntryPoint])

  const handleSuggestedActionClick = useCallback((label: string) => {
    fireEntryPoint('suggestion_chip')
    toggleTag(label)
  }, [toggleTag, fireEntryPoint])

  const handleRecentSearchClick = useCallback((locationName: string, checkInDate?: string, checkOutDate?: string, totalAdults?: number) => {
    fireEntryPoint('recent_search_card')
    const tags = getRecentSearchTags(locationName, checkInDate, checkOutDate, totalAdults)
    const allSelected = tags.every(tag => selectedTagSet.has(tag))
    if (allSelected) {
      tags.forEach(tag => onRemoveTag?.(tag))
    } else {
      onAddTags?.(tags)
    }
  }, [getRecentSearchTags, selectedTagSet, onRemoveTag, onAddTags, fireEntryPoint])

  const handleViewedOfferClick = useCallback((propertyName: string, location?: string) => {
    fireEntryPoint('viewed_offer_card')
    const tags = getViewedOfferTags(propertyName, location)
    const allSelected = tags.every(tag => selectedTagSet.has(tag))
    if (allSelected) {
      tags.forEach(tag => onRemoveTag?.(tag))
    } else {
      onAddTags?.(tags)
    }
  }, [selectedTagSet, onRemoveTag, onAddTags, fireEntryPoint])

  const recentChatItems = useMemo(() => {
    return buildRecentChatItems(data ?? [], previousTrips)
  }, [data, previousTrips])

  const visibleRecentChatItems = useMemo(() => {
    return showAllRecentChats ? recentChatItems : recentChatItems.slice(0, INITIAL_VISIBLE_RECENT_CHATS)
  }, [recentChatItems, showAllRecentChats])

  const handleRecentChatsToggle = useCallback(() => {
    setShowAllRecentChats(previous => !previous)
  }, [])

  const visibleAiChatIds = useMemo(() => {
    return visibleRecentChatItems
      .filter(item => item.type === 'aiChat')
      .map(item => item.id)
  }, [visibleRecentChatItems])

  const userMessageSubtitleByChatId = useUserMessageSubtitles(visibleAiChatIds, isLoggedIn)

  const handleRecentChatItemClick = useCallback((itemType: 'aiChat' | 'classicTrip', itemId: string | number) => {
    fireEntryPoint('recent_trip_card')

    if (itemType === 'aiChat') {
      navigateTo(`${CHAT_TRIP_PLANNER_ROUTE}/${itemId}`)
      return
    }

    navigateTo(getTripURL(String(itemId)))
  }, [fireEntryPoint, navigateTo])

  const handleRecentChatItemDeleteClick = useCallback((item: RecentChatItem, closeMenu: () => void) => async(event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    closeMenu()

    const fallbackName = intl.formatMessage({
      id: 'P7xyHLCCjSlOklqXsVYg',
      defaultMessage: 'Untitled',
      description: 'Default title for untitled trip in the trip planner feature',
    })
    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal
        name={item.title?.trim() || fallbackName}
        type={item.type === 'classicTrip' ? 'trip' : 'chat'}
      />,
    )

    if (!confirmed) {
      return
    }

    try {
      if (item.type === 'aiChat') {
        await deleteChatMutation({ chatId: item.id })
        await refetchChatList()
        return
      }

      await deleteTripMutation(item.id)
    } catch(error) {
      if (item.type === 'classicTrip') {
        // Trip deletion errors are already surfaced by useDeleteTrip's onError callback.
        return
      }

      console.error('[TripChat] Failed to delete chat from landing page', error)
      showSnackbar(intl.formatMessage({
        id: 'GjRGiJeZErGMMw8bQjrE',
        defaultMessage: 'Unable to delete chat. Please try again.',
        description: 'Error snackbar message when chat deletion fails in the trip planner AI chat',
      }), 'critical')
    }
  }, [deleteChatMutation, deleteTripMutation, intl, refetchChatList, showModal])

  const handleRecentChatItemRename = useCallback((item: RecentChatItem) => async(name: string) => {
    try {
      if (item.type === 'aiChat') {
        await renameChatMutation({ chatId: item.id, name })
        await refetchChatList()
        return
      }

      await updateTripMutation({ tripId: item.id, name })
      await refetchTrips()
    } catch(error) {
      if (item.type === 'classicTrip') {
        throw error
      }

      console.error('[TripChat] Failed to rename chat from landing page', error)
      showSnackbar(intl.formatMessage({
        id: 'tvKzJmAgeic_SZz_ioDb',
        defaultMessage: 'Unable to rename chat. Please try again.',
        description: 'Error snackbar message when chat rename fails in the trip planner AI chat',
      }), 'critical')
      throw error
    }
  }, [intl, refetchChatList, refetchTrips, renameChatMutation, updateTripMutation])

  const visibleTiles = useMemo<Array<CarouselTile>>(() => {
    switch (selectedFilter) {
      case undefined:
        return shuffledAllTiles
      case 'bookings':
        return bookingTiles.map(data => ({ type: 'booking' as const, data }))
      case 'recentSearches':
        return recentSearchTiles.map(data => ({ type: 'recentSearch' as const, data }))
      case 'viewedOffers':
        return viewedOfferIds.map(offerId => ({ type: 'viewedOffer' as const, offerId }))
      case 'suggestions':
        return suggestedActions.map(label => ({ type: 'suggestedAction' as const, label }))
    }
  }, [selectedFilter, shuffledAllTiles, bookingTiles, recentSearchTiles, viewedOfferIds, suggestedActions])

  const handleSubmitWithEntryTracking = useCallback(async(message: string) => {
    if (!entryPointFiredRef.current) {
      fireEntryPoint('typed_prompt')
    }

    if (isSubmittingBookings) {
      onSubmit(message)
      return
    }

    const selectedBookingTileItemIds = new Set<string>()
    for (const [tag, tile] of bookingDataByTag.entries()) {
      if (selectedTagSet.has(tag)) {
        selectedBookingTileItemIds.add(tile.itemId)
      }
    }

    if (selectedBookingTileItemIds.size === 0) {
      onSubmit(message)
      return
    }

    const selectedBookings = collectSelectedBookings(upcomingOrders, selectedBookingTileItemIds)
    if (selectedBookings.length === 0) {
      onSubmit(message)
      return
    }

    setIsSubmittingBookings(true)
    try {
      const bookingContextSections = await buildBookingContextSections(selectedBookings)
      if (bookingContextSections.length === 0) {
        onSubmit(message)
        return
      }

      const contextMessage = `Please add the following existing bookings to my trip:\n\n${bookingContextSections.join('\n\n')}`
      onSubmit(appendContextTag(message, contextMessage, TRIP_PLANNER_ACTION_CONTEXT_LABEL))
    } catch(error) {
      reportClientError(error, {
        message: 'Failed to build add-booking context from chat landing page booking selection',
        bookingItemIds: [...selectedBookingTileItemIds],
      })
      onSubmit(message)
    } finally {
      setIsSubmittingBookings(false)
    }
  }, [
    onSubmit,
    fireEntryPoint,
    isSubmittingBookings,
    bookingDataByTag,
    selectedTagSet,
    upcomingOrders,
  ])

  const showRecentChats = isLoggedIn && recentChatItems.length > 0
  const shouldShowRecentChatsToggle = recentChatItems.length > INITIAL_VISIBLE_RECENT_CHATS
  const recentChatsToggleIcon = <DropdownChevron open={showAllRecentChats} />
  const recentChatsToggleLabel = showAllRecentChats ? (
    <FormattedMessage
      id="FFWPIP_coIe5JKGMLdoc"
      defaultMessage="View fewer"
      description="Button text to collapse the recent chats section on the trip planner chat landing page"
    />
  ) : (
    <FormattedMessage
      id="Xnu4qAlVRhA2_qGS8dxS"
      defaultMessage="View all {count} chats"
      description="Button text to expand the recent chats section on the trip planner chat landing page"
      values={{ count: recentChatItems.length }}
    />
  )
  const showStaffChatAccess = isLoggedIn && isStaff && canAccessStaffChat(accountEmail) && !!onStaffChatOpen
  return (
    <LandingContainer className={className}>
      <TopArea>
        <ChatLandingFloatingImages />
        <ContentWrapper variant="medium">
          <Group direction="vertical" gap={40} fullWidth>
            <Group direction="vertical" gap={20} fullWidth>
              <ChatLandingHero
                onSubmit={handleSubmitWithEntryTracking}
                disabled={disabled}
                selectedTags={selectedTags}
                selectedBookingItemIds={selectedBookingItemIds}
                onBookingItemsAdded={handleBookingItemsAdded}
                onRemoveTag={onRemoveTag}
              />
            </Group>
            {filterOptions.length > 0 && (
              <Group direction="vertical" gap={12} fullWidth>
                <BodyText variant="medium" weight="bold" align="start">
                  <FormattedMessage
                    id="v5ph4Ew9wkBgWmb7V0Zo"
                    defaultMessage="Ideas to get started"
                    description="Heading for the card filter section on the chat landing page"
                  />
                </BodyText>
                <HorizontalContainerWithFadedOverflow>
                  <Group direction="horizontal" gap={8}>
                    {filterOptions.map(option => (
                      <FilterChip
                        key={option.id}
                        data-filter={option.id}
                        selected={option.id === selectedFilter}
                        onClick={handleFilterClick}
                        size="medium"
                      >
                        {option.title}
                      </FilterChip>
                    ))}
                  </Group>
                </HorizontalContainerWithFadedOverflow>
                <HorizontalContainerWithFadedOverflow gap={12} overflowGutter={0}>
                  {visibleTiles.map(tile => {
                    switch (tile.type) {
                      case 'booking': {
                        const bookingTag = bookingTagsByItemId.get(tile.data.itemId) ?? ''
                        const isSelectedBookingTile = selectedTagSet.has(bookingTag)
                        const isModalAddedBookingTile = modalAddedBookingItemIdSet.has(tile.data.itemId)
                        const isBookingTileDisabled =
                          isModalAddedBookingTile &&
                          !isSelectedBookingTile

                        if (tile.data.bookingType === 'flight') {
                          return (
                            <ChatLandingFlightOrderTile
                              key={tile.data.itemId}
                              itemId={tile.data.itemId}
                              orderId={tile.data.orderId}
                              selected={isSelectedBookingTile}
                              disabled={isBookingTileDisabled}
                              onClick={handleBookingTileClick}
                            />
                          )
                        }
                        if (tile.data.bookingType === 'experience') {
                          return (
                            <ChatLandingExperienceOrderTile
                              key={tile.data.itemId}
                              itemId={tile.data.itemId}
                              experienceId={tile.data.offerId}
                              propertyName={tile.data.propertyName}
                              checkInDate={tile.data.checkInDate}
                              time={tile.data.time}
                              selected={isSelectedBookingTile}
                              disabled={isBookingTileDisabled}
                              onClick={handleBookingTileClick}
                            />
                          )
                        }
                        return (
                          <ChatLandingUpcomingOrderTile
                            key={tile.data.itemId}
                            itemId={tile.data.itemId}
                            propertyName={tile.data.propertyName}
                            imageId={tile.data.imageId}
                            checkInDate={tile.data.checkInDate}
                            checkOutDate={tile.data.checkOutDate}
                            occupancy={tile.data.occupancy}
                            selected={isSelectedBookingTile}
                            disabled={isBookingTileDisabled}
                            onClick={handleBookingTileClick}
                          />
                        )
                      }
                      case 'recentSearch':
                        return (
                          <ChatLandingRecentSearchTile
                            key={tile.data.id}
                            locationName={tile.data.locationName}
                            checkInDate={tile.data.checkInDate}
                            checkOutDate={tile.data.checkOutDate}
                            totalAdults={tile.data.totalAdults}
                            selected={getRecentSearchTags(tile.data.locationName, tile.data.checkInDate, tile.data.checkOutDate, tile.data.totalAdults).every(tag => selectedTagSet.has(tag))}
                            onClick={handleRecentSearchClick}
                          />
                        )
                      case 'viewedOffer':
                        return (
                          <ChatLandingViewedOfferTile
                            key={tile.offerId}
                            offerId={tile.offerId}
                            selectedTagSet={selectedTagSet}
                            onClick={handleViewedOfferClick}
                          />
                        )
                      case 'suggestedAction':
                        return (
                          <ChatLandingSuggestedActionTile
                            key={tile.label}
                            label={tile.label}
                            selected={selectedTagSet.has(tile.label)}
                            onClick={handleSuggestedActionClick}
                          />
                        )
                      default:
                        return null
                    }
                  })}
                </HorizontalContainerWithFadedOverflow>
              </Group>
            )}
          </Group>
        </ContentWrapper>
      </TopArea>
      {showRecentChats && (
        <RecentChatsSection>
          <RecentChatsContent>
            <Group direction="vertical" gap={20} fullWidth>
              <BodyText variant="large" weight="bold" align="start">
                <FormattedMessage
                  id="5MZf0UAxaeIPnUE6edFv"
                  defaultMessage="Recent chats"
                  description="Heading for recent chats section in the trip planner feature"
                />
              </BodyText>
              <RecentChatsGrid>
                {visibleRecentChatItems.map(item => (
                  <ChatLandingRecentChatTile
                    key={`${item.type}-${item.id}`}
                    title={item.title}
                    subtitle={item.type === 'aiChat' ? userMessageSubtitleByChatId.get(item.id) : item.subtitle}
                    updatedAt={item.updatedAt}
                    onClick={() => handleRecentChatItemClick(item.type, item.id)}
                    onRename={handleRecentChatItemRename(item)}
                    onDeleteClick={(closeMenu) => handleRecentChatItemDeleteClick(item, closeMenu)}
                  />
                ))}
              </RecentChatsGrid>
              {shouldShowRecentChatsToggle && (
                <Group direction="vertical" horizontalAlign="center">
                  <TextButton
                    kind="secondary"
                    size="medium"
                    shape="round"
                    onClick={handleRecentChatsToggle}
                    endIcon={recentChatsToggleIcon}
                  >
                    {recentChatsToggleLabel}
                  </TextButton>
                </Group>
              )}
            </Group>
          </RecentChatsContent>
        </RecentChatsSection>
      )}
      {showStaffChatAccess && (
        <StaffToolsSection>
          <StaffToolsContent>
            <StaffChatAccessForm onOpenChat={onStaffChatOpen} />
          </StaffToolsContent>
        </StaffToolsSection>
      )}
    </LandingContainer>
  )
}

export default ChatLandingPage
